import { type ReactNode } from 'react'
import { Magnetic } from '#app/components/ui/magnetic.tsx'
import { Reveal } from '#app/components/ui/reveal.tsx'
import { WordReveal } from '#app/components/ui/word-reveal.tsx'

/**
 * The closing call-to-action band of a landing page: brand gradient, a soft
 * orb, heading, text and an action slot. Takes already-translated content, so
 * the routes keep their `t(...)` calls and their tracking. A text heading
 * reveals word by word, and the action leans towards the mouse.
 */
export function CtaBand({
	heading,
	text,
	action,
}: {
	heading: ReactNode
	text: ReactNode
	action: ReactNode
}) {
	const title = (
		<h2 className="text-h2 text-brand-foreground">
			{typeof heading === 'string' ? <WordReveal text={heading} /> : heading}
		</h2>
	)

	return (
		<section data-slot="cta-band" className="relative overflow-hidden py-24">
			<div className="bg-gradient-brand absolute inset-0" aria-hidden="true" />
			<div
				className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-white/10 blur-3xl"
				aria-hidden="true"
			/>

			<div className="relative z-10 container text-center">
				{typeof heading === 'string' ? title : <Reveal>{title}</Reveal>}
				<Reveal delay={100}>
					<p className="text-body-lg text-brand-foreground/80 mx-auto mt-4 max-w-2xl">
						{text}
					</p>
				</Reveal>
				<Reveal delay={200}>
					<div className="mt-10 flex flex-col gap-4 sm:flex-row sm:justify-center">
						<Magnetic>{action}</Magnetic>
					</div>
				</Reveal>
			</div>
		</section>
	)
}
